import { Property } from '@propertypal/shared/src/types/property';
import getSellingAgent from '@propertypal/shared/src/utils/property/getSellingAgent';
import { BASE_URL } from '../components/layout/MetaTags';

const buildPropertySchema = (property: Property): Record<string, unknown> => {
  const canonicalUrl = `${BASE_URL}${property.path}`;
  const image = property.images?.length ? property.images[0].url : undefined;
  const listingPrice = property.price?.price ?? property.price?.minPrice ?? property.price?.maxPrice;
  const availability = ['saleAgreed', 'letAgreed', 'sold', 'let'].includes(property.status?.key || '')
    ? 'https://schema.org/SoldOut'
    : 'https://schema.org/InStock';
  const addressCountry = property.country?.isoCode2 || property.countryCode;
  const sellingAgent = getSellingAgent(property);

  const listing: Record<string, unknown> = {
    '@type': 'RealEstateListing',
    '@id': property.pathId,
    url: canonicalUrl,
    name: property.metaDescriptions.title,
    description: property.metaDescriptions.description,
    datePosted: property.activationTime,
    dateModified: property.listingUpdatedTime,
    image,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonicalUrl,
    },
    seller: {
      '@type': 'RealEstateAgent',
      name: sellingAgent?.organisation,
      url: sellingAgent?.websiteUrl,
    },
    offers:
      typeof listingPrice === 'number'
        ? {
            '@type': 'Offer',
            price: listingPrice,
            priceCurrency: property.price?.currencyCode || 'GBP',
            availability,
          }
        : undefined,
    itemOffered: {
      '@type': property.style?.key.includes('apartment') ? 'Apartment' : 'House',
      name: property.displayAddress,
      numberOfBedrooms: property.numBedrooms,
      numberOfBathroomsTotal: property.numBathrooms,
      address: {
        '@type': 'PostalAddress',
        streetAddress: property.displayAddressLine1,
        addressLocality: property.town,
        addressRegion: property.region,
        postalCode: property.postcode,
        addressCountry,
      },
      geo: property.coordinate
        ? {
            '@type': 'GeoCoordinates',
            latitude: property.coordinate.latitude,
            longitude: property.coordinate.longitude,
          }
        : undefined,
    },
  };

  const breadcrumbs =
    property.breadcrumbs?.length > 0
      ? {
          '@type': 'BreadcrumbList',
          itemListElement: property.breadcrumbs.map((crumb, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: crumb.label,
            item: `${BASE_URL}${crumb.url}`,
          })),
        }
      : undefined;

  return {
    '@context': 'https://schema.org',
    '@graph': [listing, breadcrumbs].filter(Boolean),
  };
};

export default buildPropertySchema;
